import { CommonModule, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn } from '@angular/forms';

import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { merge } from 'lodash';

import { FormValidations } from '@hmax-order/frontend/utils';
import { DateUtils } from '@hmax-order/shared/date';
import { DialogPeriodForm, PeriodConfig } from '../../interfaces';
import { InputDateComponent, InputDateRangeComponent } from '../fields';

@Component({
  selector: 'hmax-period-control',
  standalone: true,
  templateUrl: './period-control.component.html',
  styleUrls: ['./period-control.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, InputDateComponent, InputDateRangeComponent, ReactiveFormsModule, TranslocoModule, TitleCasePipe],
})
export class PeriodControlComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() config!: PeriodConfig;

  min!: Date;
  max!: Date;
  dateRangeLabel = '';
  baseConfig!: PeriodConfig;
  formControl: DialogPeriodForm = {
    start: {
      label: 'start',
      labelTranslate: true,
      name: 'start',
      validators: [],
    },
    end: {
      label: 'end',
      labelTranslate: true,
      name: 'end',
      validators: [],
    },
  };

  private _formBuilder = inject(FormBuilder);
  private _translocoService = inject(TranslocoService);

  ngOnInit(): void {
    const start = merge(this.formControl?.start, this.config.formControl?.start);
    const end = merge(this.formControl?.end, this.config.formControl?.end);
    const formControl = { start, end };
    this.baseConfig = { ...this.config, formControl };
    this.verifyConfig();
    this.createFormControls();
    this.checkDateRangeLabel();
  }

  private verifyConfig(): void {
    const currentDate = DateUtils.currentDate();

    if (!this.baseConfig?.notMinDate) {
      const minDays = this.baseConfig?.minDays ?? 30;
      this.min = this.baseConfig?.min ?? currentDate.minus({ days: minDays }).toJSDate();
    }

    if (!this.baseConfig?.notMaxDate) {
      const maxDays = this.baseConfig?.maxDays ? currentDate.plus({ days: this.config.maxDays }).toJSDate() : currentDate.toJSDate();
      this.max = this.baseConfig?.max ?? maxDays;
    }
  }

  private createFormControls(): void {
    const start = this.baseConfig?.formControl?.start;
    const end = this.baseConfig?.formControl?.end;

    if (start && end) {
      const startValidators = this.config.maxPeriodDays
        ? [FormValidations.verifyMaxPeriodDays('start', String(end.name), this.config.maxPeriodDays), ...(start?.validators ?? [])]
        : (start?.validators ?? []);
      const endValidators = this.config.maxPeriodDays
        ? [FormValidations.verifyMaxPeriodDays('end', String(start.name), this.config.maxPeriodDays), ...(end?.validators ?? [])]
        : (end?.validators ?? []);

      this.form.addControl(
        String(start?.label),
        this._formBuilder.control(start?.value ?? null, { validators: startValidators as ValidatorFn[] }),
      );
      this.form.addControl(
        String(end?.label),
        this._formBuilder.control(end?.value ?? null, { validators: endValidators as ValidatorFn[] }),
      );
    }
  }

  private checkDateRangeLabel(): void {
    let label = '';

    label += this.formControl?.start?.labelTranslate ? this.translate(this.formControl?.start?.label) : this.formControl?.start?.label;

    label += ' - ';

    label += this.formControl?.end?.labelTranslate ? this.translate(this.formControl?.end?.label) : this.formControl?.end?.label;

    this.dateRangeLabel = label;
  }

  private translate(text?: string): string {
    return text ? this._translocoService.translate(text) : String(text ?? '');
  }
}
